<template>
  <div class="room-card">
    <!-- CONTEÚDO DO TOPO -->
    <b-row>
      <b-col cols="11">
        <Heading element="h4" level="4">{{ roomData.nome }}</Heading>
      </b-col>

      <b-col class="remove-col" cols="1">
        <button class="remove-col--btn" @click="removeFirstRule()">
          <Icon icon="x-icon" />
        </button>
      </b-col>
    </b-row>

    <!-- CAMPOS DE EDIÇÃO -->
    <div class="rules-fields">
      <transition-group name="fade" appear>
        <RoomCardRule
          v-for="(rule, i) in rules"
          :value="rules[i]"
          :key="`rule-${roomData.codigo}-${i}`"
          :order="i"
          :typeUpdate="typeUpdate"
          :weekDaysOptions="getAvailableWeekDays(rules[i].selectedWeekDays)"
          :roomCode="roomData.codigo"
          :adults="roomData.maximoAdultos"
          :children="roomData.maximoCriancas"
          class="rules-fields--rule"
          @delete="removeRule"
        />
      </transition-group>
    </div>

    <b-row class="mt-3">
      <b-col cols="2">
        <Button
          v-show="rules.length < 7"
          layout="outline"
          size="large"
          hasIcon
          @click="addRule()"
          >Nova regra</Button
        >
      </b-col>
    </b-row>
  </div>
</template>

<script>
/**
 * Card para configurar os valores de um quarto
 *
 * Regras: https://www.figma.com/file/9AFe6i7bVhMup1bYeppCU7/Tarifas?node-id=22%3A98
 */
import Heading from '@/components/_base/elements/Heading'
import Button from '@/components/_base/elements/Button'
import RoomCardRule from './RoomCardRule'

export default {
  name: 'RoomCard',
  components: {
    Heading,
    Button,
    RoomCardRule
  },
  props: {
    /**
     * Dados do quarto
     */
    roomData: {
      type: Object,
      default: () => {}
    },
    /**
     * Tipo de atualização
     */
    typeUpdate: {
      type: Number,
      default: 1
    },
    /**
     * Dias da semana disponíveis
     * Na API do letsbook o padrão é 1-Domingo a 7-Sábado
     */
    weekDaysOptions: {
      type: Array,
      default: () => [
        { text: 'Domingo', value: 1 },
        { text: 'Segunda', value: 2 },
        { text: 'Terça', value: 3 },
        { text: 'Quarta', value: 4 },
        { text: 'Quinta', value: 5 },
        { text: 'Sexta', value: 6 },
        { text: 'Sábado', value: 7 }
      ]
    },
    /** Array com as regras definidas */
    value: {
      type: Array,
      default: () => []
    },
    /** Valor cacheado da última edição feita */
    cachedValue: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      /** Inicializa com valores cacheados se existirem */
      rules: this.cachedValue?.rules || []
    }
  },
  watch: {
    /**
     * Verifica alterações em profundidade no array de regras
     * Emite um evento de mudança
     *
     * ? pensar em uma forma de melhorar essa parte
     * */
    rules: {
      handler(newValue) {
        const room = {
          code: this.roomData.codigo,
          rules: newValue
        }

        const ruleIndex = this.value.findIndex(
          (item) => item.code === room.code
        )
        if (ruleIndex > -1) {
          const newArrayValue = [...this.value]
          newArrayValue[ruleIndex] = room

          this.$emit('input', newArrayValue)
        } else {
          this.$emit('input', [...this.value, room])
        }
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    if (!this.cachedValue) this.addRule()
  },
  methods: {
    /**
     * Adiciona uma nova regra ao quarto
     */
    addRule() {
      let newRule = {
        roomCode: this.roomData.codigo,
        selectedWeekDays: [],
        desconto: null
      }

      newRule = this.createGuestFields(newRule)

      this.rules.push(newRule)
    },
    /**
     * Remove a primeira regra da listagem ou o quarto quando só possuí uma regra
     */
    removeFirstRule() {
      if (this.rules.length === 1) {
        this.$emit('remove', this.roomData)
        return
      }

      this.rules.splice(0, 1)
    },
    /**
     * Remove a regra da da posição passada da listagem
     * @param {Number} order posição dentro do array de regras
     */
    removeRule(order) {
      this.rules.splice(order, 1)
    },
    /**
     * Cria os campos de hóspedes dinâmicamente
     */
    createGuestFields(newRule) {
      /**
       * Cria propriedades para adultos
       */
      for (let i = 0; i < this.roomData.maximoAdultos; i++) {
        newRule[`pax${i + 1}`] = null
      }

      /**
       * Cria propriedades para crianças
       */
      for (let i = 0; i < this.roomData.maximoCriancas; i++) {
        newRule[`criancas${i + 1}`] = null
      }

      return newRule
    },
    /**
     * Pega os dias disponíveis para aplicar na regra
     *
     * @param {Array} ignoreDays dias para serem ignorados e retornados como disponíveis
     * @returns Array de dias disponíveis ordenados em ordem crescente
     */
    getAvailableWeekDays(ignoreDays = null) {
      let available = []

      this.weekDaysOptions.forEach((day) => {
        const unavailableDay = this.rules
          .flatMap((rule) => rule.selectedWeekDays)
          .find((selDay) => selDay.value === day.value)

        if (!unavailableDay) {
          available.push(day)
        }
      })

      if (ignoreDays) {
        available = [
          ...available,
          ...ignoreDays.filter((ignored) => available.value !== ignored.value)
        ].sort((a, b) => a.value - b.value)
      }

      return available
    }
  }
}
</script>

<style lang="scss" scoped>
.room-card {
  padding: 22px;
  border: 1px solid $gray-border;
}

.remove-col {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;

  &--btn {
    @include reset-button();

    .icon {
      width: 12px;
      height: 12px;
      color: $gray-dark;
    }
  }
}

.rules-fields {
  margin-top: 25px;

  &--rule {
    & + & {
      padding-top: 40px;
      border-top: 1px solid $gray-border;
    }
  }
}
</style>
